/*
 * Ghidul de folosire și butonul „Ajutor” (31 oct 2026, Dan: „o secțiune unde să avem instrucțiuni de folosire a
 * aplicației și, dacă un mecanic nu înțelege ceva, să poată avea un help care să-l ajute”).
 *
 * Sursa unică e static/ghid/ghid.json, servită prin /api/ghid (nu din /static/, pe care service worker-ul îl ține în
 * cache). Din ea se iau: pagina „Ghid de folosire” din meniu, panoul „Ajutor” de pe fiecare ecran (întâi situațiile
 * ecranului) și unealta `ghid_aplicatie` a chatului. Dacă ghidul nu ajunge: «Întreabă AI-ul» (chatul, care citește
 * același ghid) sau «Cere ajutor inginerului» (cererile de ajutor existente, /api/help_requests).
 * Plasa care ține ghidul la zi cu butoanele de pe ecran: tests/test_ghid.py.
 */
import { api, escHtml, state, toast } from './core';

interface Situatie { id: string; intrebare: string; pasi: string[]; butoane: string[]; cuvinte?: string[] }
interface Sectiune { id: string; titlu: string; ecran: string; pentru: 'mecanic' | 'inginer' | 'toti'; situatii: Situatie[] }
interface Ghid { versiune: string; despre: string; sectiuni: Sectiune[] }

let _ghid: Ghid | null = null;

async function _incarca(): Promise<Ghid | null> {
    if (_ghid) return _ghid;
    try {
        _ghid = await api('/api/ghid');
    } catch {
        _ghid = null;
    }
    return _ghid;
}

function _fold(t: string): string {
    return (t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Butoanele citate «așa» ies îngroșate, ca omul să le recunoască pe ecran. */
function _pas(t: string): string {
    return escHtml(t).replace(/«([^»]+)»/g, '<b class="ghid-buton">«$1»</b>');
}

function _situatieHtml(s: Sectiune, x: Situatie, deschisa = false): string {
    return `<details class="ghid-situatie" ${deschisa ? 'open' : ''} data-id="${escHtml(s.id + '/' + x.id)}"
        style="border:1px solid var(--border);border-radius:10px;padding:10px 12px;margin-bottom:8px;background:var(--card-bg)">
        <summary style="cursor:pointer;font-weight:700;font-size:1rem;min-height:32px">${_pas(x.intrebare)}</summary>
        <ol style="margin:10px 0 2px 18px;line-height:1.5">${x.pasi.map(p => `<li>${_pas(p)}</li>`).join('')}</ol>
    </details>`;
}

/** Potrivirea căutării: toate cuvintele (fără diacritice, primele 5 litere) trebuie să apară în situație. */
function _potrivire(x: Situatie, s: Sectiune, cauta: string): boolean {
    const corp = _fold([x.intrebare, s.titlu, ...(x.cuvinte || []), ...x.pasi].join(' '));
    const cuv = _fold(cauta).split(/[^a-z0-9]+/).filter(w => w.length >= 3).map(w => w.slice(0, 5));
    return cuv.length > 0 && cuv.every(w => corp.includes(w));
}

function _rolulMeu(): 'mecanic' | 'inginer' {
    const r = state.currentUser?.role || '';
    return r === 'engineer' || r === 'master' ? 'inginer' : 'mecanic';
}

async function _cereAjutorInginer(ecran: string, intrebare: string): Promise<void> {
    if (!intrebare.trim()) { toast('Scrie întâi ce nu înțelegi', 'error'); return; }
    try {
        await api('/api/help_requests', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: `[Ajutor aplicație · ${ecran}] ${intrebare.trim()}`,
                requested_by: state.currentUser?.username || '' }),
        });
        toast('Am trimis întrebarea inginerului — răspunsul vine la cererile de ajutor', 'success');
    } catch (e: unknown) {
        toast('Cererea n-a plecat: ' + (e instanceof Error ? e.message : String(e)), 'error');
    }
}

function _intreabaAI(intrebare: string): void {
    const q = intrebare.trim();
    showSection('sec-chat');
    setTimeout(() => {
        const camp = document.getElementById('chat-input') as HTMLInputElement | null;
        if (camp) { camp.value = q ? `Despre aplicație: ${q}` : 'Despre aplicație: '; camp.focus(); }
    }, 400);
}

/** Bara de jos, comună paginii și panoului: când ghidul nu ajunge. */
function _barNuGasesc(id: string): string {
    return `<div style="margin-top:12px;padding:12px;border:1px dashed var(--border);border-radius:10px">
        <div style="font-weight:700;margin-bottom:8px">Nu găsești răspunsul?</div>
        <textarea class="dx-input" id="${id}-intrebare" rows="2" placeholder="Scrie pe scurt ce nu înțelegi"
            style="width:100%;min-height:60px"></textarea>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
            <button class="dx-btn-primary" id="${id}-ai" style="min-height:44px;padding:10px 16px">🤖 Întreabă AI-ul</button>
            <button class="dx-btn-primary" id="${id}-inginer" style="min-height:44px;padding:10px 16px;background:var(--card-bg);
                border:1px solid var(--border);color:var(--text)">📞 Cere ajutor inginerului</button>
        </div>
    </div>`;
}

function _legaNuGasesc(radacina: HTMLElement, id: string, ecran: string, inchide?: () => void): void {
    const text = () => (radacina.querySelector(`#${id}-intrebare`) as HTMLTextAreaElement | null)?.value || '';
    radacina.querySelector(`#${id}-ai`)?.addEventListener('click', () => { const q = text(); inchide?.(); _intreabaAI(q); });
    radacina.querySelector(`#${id}-inginer`)?.addEventListener('click', () => { void _cereAjutorInginer(ecran, text()); });
}

/* ═══ Pagina „Ghid de folosire” ═════════════════════════════════════════════════════════════════════════════════ */
export async function initGhid(): Promise<void> {
    const sec = document.getElementById('sec-ghid');
    if (!sec) return;
    sec.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span> Se încarcă ghidul…</div>';
    const g = await _incarca();
    if (!g) { sec.innerHTML = '<div class="alarm-empty"><span class="icon">❌</span> Ghidul nu s-a încărcat — reîncarcă pagina.</div>'; return; }
    let filtru: 'toti' | 'mecanic' | 'inginer' = _rolulMeu() === 'inginer' ? 'toti' : 'mecanic';
    sec.innerHTML = `
        <div class="section-header"><h2>Ghid de folosire</h2></div>
        <div style="max-width:860px">
            <div class="ss-sub" style="margin-bottom:10px">Cum faci lucrurile în aplicație, pas cu pas. Butoanele sunt scrise
                «așa», exact ca pe ecran. Același ghid îl ai și la «Ajutor», pe fiecare ecran.</div>
            <input class="dx-input" id="ghid-cauta" type="search" placeholder="Ce vrei să faci? (ex: am apăsat din greșeală, amân o lucrare)"
                style="width:100%;min-height:44px;margin-bottom:10px">
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px" id="ghid-filtre">
                <button class="btn btn-sm ghid-filtru" data-f="mecanic">Mecanic</button>
                <button class="btn btn-sm ghid-filtru" data-f="inginer">Inginer</button>
                <button class="btn btn-sm ghid-filtru" data-f="toti">Tot ghidul</button>
            </div>
            <div id="ghid-lista"></div>
            ${_barNuGasesc('ghid-pag')}
            <div class="ss-sub" style="margin-top:10px">Ghidul din ${escHtml(g.versiune)}.</div>
        </div>`;
    const lista = sec.querySelector('#ghid-lista') as HTMLElement;
    const cauta = sec.querySelector('#ghid-cauta') as HTMLInputElement;
    const deseneaza = () => {
        const q = cauta.value.trim();
        sec.querySelectorAll('.ghid-filtru').forEach(b => {
            const activ = (b as HTMLElement).dataset.f === filtru;
            b.classList.toggle('btn-primary', activ);
            b.classList.toggle('btn-outline', !activ);
        });
        let html = '';
        g.sectiuni.forEach(s => {
            if (!q && filtru !== 'toti' && s.pentru !== 'toti' && s.pentru !== filtru) return;
            const sit = s.situatii.filter(x => !q || _potrivire(x, s, q));
            if (!sit.length) return;
            html += `<h3 style="margin:14px 0 8px">${escHtml(s.titlu)}</h3>` + sit.map(x => _situatieHtml(s, x, !!q)).join('');
        });
        lista.innerHTML = html || '<div class="alarm-empty"><span class="icon">🔍</span> Nimic în ghid pentru căutarea asta — întreabă mai jos.</div>';
    };
    cauta.addEventListener('input', deseneaza);
    sec.querySelectorAll('.ghid-filtru').forEach(b => b.addEventListener('click', () => {
        filtru = ((b as HTMLElement).dataset.f || 'toti') as typeof filtru;
        deseneaza();
    }));
    _legaNuGasesc(sec, 'ghid-pag', 'Ghid de folosire');
    deseneaza();
}

/* ═══ Panoul „Ajutor” de pe ecrane ═════════════════════════════════════════════════════════════════════════════ */
export async function deschideAjutor(ecran: string): Promise<void> {
    const g = await _incarca();
    if (!g) { toast('Ghidul nu s-a încărcat — încearcă din nou', 'error'); return; }
    const aleEcranului = g.sectiuni.filter(s => s.ecran === ecran);
    const overlay = document.createElement('div');
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `<div class="proc-container" style="max-width:640px;max-height:90vh;display:flex;flex-direction:column" role="dialog" aria-modal="true">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title">❓ Ajutor${aleEcranului.length ? ' — ' + escHtml(aleEcranului[0].titlu) : ''}</h2>
            <button class="proc-close" id="ajutor-x" aria-label="Închide">&times;</button></div></div>
        <div style="padding:14px 16px;overflow:auto">
            <input class="dx-input" id="ajutor-cauta" type="search" placeholder="Ce vrei să faci?" style="width:100%;min-height:44px;margin-bottom:10px">
            <div id="ajutor-lista"></div>
            ${_barNuGasesc('ajutor')}
            <div style="margin-top:10px"><a href="#" id="ajutor-tot">Tot ghidul («Ghid de folosire»)</a></div>
        </div></div>`;
    document.body.appendChild(overlay);
    const lista = overlay.querySelector('#ajutor-lista') as HTMLElement;
    const cauta = overlay.querySelector('#ajutor-cauta') as HTMLInputElement;
    const inchide = () => overlay.remove();
    const deseneaza = () => {
        const q = cauta.value.trim();
        const surse = q ? g.sectiuni : (aleEcranului.length ? aleEcranului : g.sectiuni);
        let html = '';
        surse.forEach(s => s.situatii.filter(x => !q || _potrivire(x, s, q))
            .forEach(x => { html += _situatieHtml(s, x, !!q); }));
        lista.innerHTML = html || '<div class="ss-sub">Nimic în ghid pentru asta — întreabă mai jos.</div>';
    };
    cauta.addEventListener('input', deseneaza);
    overlay.querySelector('#ajutor-x')!.addEventListener('click', inchide);
    overlay.addEventListener('click', e => { if (e.target === overlay) inchide(); });
    overlay.querySelector('#ajutor-tot')!.addEventListener('click', e => { e.preventDefault(); inchide(); showSection('sec-ghid'); });
    _legaNuGasesc(overlay, 'ajutor', ecran, inchide);
    deseneaza();
}

/** Pune butonul „Ajutor” în capul secțiunii (o singură dată; secțiunile care își rescriu conținutul îl primesc iar). */
export function ataseazaAjutor(sectiune: string): void {
    if (!sectiune || sectiune === 'ghid') return;
    const sec = document.getElementById('sec-' + sectiune);
    if (!sec || sec.querySelector('.ghid-ajutor-btn')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-sm btn-outline ghid-ajutor-btn';
    btn.textContent = '❓ Ajutor';
    btn.title = 'Cum se folosește ecranul acesta';
    btn.style.cssText = 'min-height:40px;padding:6px 12px;margin-left:auto;flex:none';
    btn.addEventListener('click', () => { void deschideAjutor(sectiune); });
    const cap = sec.querySelector('.op-section-header, .section-header, .chat-header') as HTMLElement | null;
    if (cap) {
        if (getComputedStyle(cap).display !== 'flex') { cap.style.display = 'flex'; cap.style.alignItems = 'center'; cap.style.gap = '8px'; cap.style.flexWrap = 'wrap'; }
        cap.appendChild(btn);
    } else {
        const bara = document.createElement('div');
        bara.style.cssText = 'display:flex;justify-content:flex-end;margin-bottom:6px';
        bara.appendChild(btn);
        sec.insertBefore(bara, sec.firstChild);
    }
}
